import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as inventoryService from "../service/inventoryService";
import type { InventoryState } from "../types";
import i18n from "@/i18n";

export const fetchItems = createAsyncThunk("inventory/fetchItems", async (institutionId: number) => {
  return await inventoryService.fetchItems(institutionId);
});

export const createItem = createAsyncThunk(
  "inventory/createItem",
  async ({ institutionId, payload }: { institutionId: number; payload: Parameters<typeof inventoryService.createItem>[1] }, { dispatch }) => {
    await inventoryService.createItem(institutionId, payload);
    await dispatch(fetchItems(institutionId));
  }
);

export const recordTransaction = createAsyncThunk(
  "inventory/recordTransaction",
  async (
    {
      institutionId,
      payload,
    }: { institutionId: number; payload: { itemId: number; direction: "IN" | "OUT"; quantity: number; reason?: string; referenceNo?: string } },
    { dispatch }
  ) => {
    await inventoryService.recordTransaction(institutionId, payload);
    await dispatch(fetchItems(institutionId));
  }
);

export const fetchItemTransactions = createAsyncThunk(
  "inventory/fetchItemTransactions",
  async ({ institutionId, itemId }: { institutionId: number; itemId: number }) => {
    return await inventoryService.fetchItemTransactions(institutionId, itemId);
  }
);

export const issueToStudent = createAsyncThunk(
  "inventory/issueToStudent",
  async (
    { institutionId, payload }: { institutionId: number; payload: Parameters<typeof inventoryService.issueToStudent>[1] },
    { dispatch }
  ) => {
    await inventoryService.issueToStudent(institutionId, payload);
    await dispatch(fetchItems(institutionId));
  }
);

export const fetchStudentIssues = createAsyncThunk(
  "inventory/fetchStudentIssues",
  async ({ institutionId, studentId, academicYearId }: { institutionId: number; studentId: number; academicYearId?: number }) => {
    return await inventoryService.fetchStudentIssues(institutionId, studentId, academicYearId);
  }
);

const initialState: InventoryState = {
  items: [],
  itemTransactions: [],
  studentIssues: [],
  loading: false,
  saving: false,
  error: null,
};

const inventorySlice = createSlice({
  name: "inventory",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToLoadItems");
      })
      .addCase(createItem.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToAddItem");
      })
      .addCase(recordTransaction.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(recordTransaction.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(recordTransaction.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToRecordTransaction");
      })
      .addCase(fetchItemTransactions.fulfilled, (state, action) => {
        state.itemTransactions = action.payload;
      })
      .addCase(issueToStudent.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToIssueItem");
      })
      .addCase(fetchStudentIssues.fulfilled, (state, action) => {
        state.studentIssues = action.payload;
      });
  },
});

export default inventorySlice.reducer;
